/* saif-plans.js — payment-plan visibility + selection (SAIF_GEOGATE_V1).
 *
 * Plans are shown only in allowed regions. The worker decides (GET /geo/plans reads the
 * caller's network country); the storefront country picker is currency-only by design,
 * so nothing here reads localization. Fail-closed: if the lookup errors or times out,
 * plan options simply stay hidden and pay-in-full works untouched.
 *
 * QA: append ?saifgeo=PK (any two-letter code) to preview that country's view; the
 * override sticks for the tab session so product -> cart keeps it. Visibility-only.
 *
 * Product page: revealing #SaifPlanPicker and stamping the chosen selling_plan into
 * every add-to-cart form for that variant (hero, bottom CTA, sticky bar).
 * Cart page: [data-saif-plan-switch] buttons rewrite a line via /cart/change.js —
 * to a plan, or back to pay-in-full — with a remove+re-add fallback for engines that
 * reject selling_plan on change.
 */
(function () {
  'use strict';
  if (window.SaifPlans) return;
  window.SaifPlans = true;

  var GEO_URL = 'https://api.saifvoice.net/geo/plans';
  var CACHE_KEY = 'saifGeoPlansV1';
  var QA_KEY = 'saifGeoQA';

  function qaCountry() {
    try {
      var m = window.location.search.match(/[?&]saifgeo=([A-Za-z]{2})/);
      if (m) sessionStorage.setItem(QA_KEY, m[1].toUpperCase());
      return sessionStorage.getItem(QA_KEY) || '';
    } catch (e) { return ''; }
  }

  function getGeo(cb) {
    var qa = qaCountry();
    if (!qa) {
      try {
        var cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) { cb(JSON.parse(cached)); return; }
      } catch (e) { /* storage blocked — just fetch */ }
    }
    fetch(qa ? GEO_URL + '?qa=' + qa : GEO_URL)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || typeof j.plans !== 'boolean') { cb(null); return; }
        if (!qa) { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(j)); } catch (e) {} }
        cb(j);
      })
      .catch(function () { cb(null); });
  }

  function wirePicker() {
    /* Every plan picker on the page — the course hero's #SaifPlanPicker AND the
       booking flow's per-service SaifPlanPickerBooking-* instances. The original
       #SaifPlanPicker-only lookup left booking pickers unwired: radios flipped
       natively but the selected ring never moved, so the cards read as dead. */
    var pickers = document.querySelectorAll('fieldset.saif-plans[data-saif-plans]');
    Array.prototype.forEach.call(pickers, function (picker) {
      if (picker.getAttribute('data-saif-wired') === '1') return;
      picker.setAttribute('data-saif-wired', '1');

      function apply(planId) {
        /* Read live — the pair-chooser (SAIF_BUNDLE_V1) retargets data-variant-id.
           Form stamping only ever matches on product pages (booking has no cart
           form for the service variant), so it is a harmless no-op elsewhere. */
        var variantId = picker.getAttribute('data-variant-id');
        var forms = document.querySelectorAll('form[action*="/cart/add"]');
        Array.prototype.forEach.call(forms, function (form) {
          var idInput = form.querySelector('input[name="id"]');
          if (!idInput || idInput.value !== variantId) return;
          var sp = form.querySelector('input[name="selling_plan"]');
          if (planId) {
            if (!sp) {
              sp = document.createElement('input');
              sp.type = 'hidden';
              sp.name = 'selling_plan';
              form.appendChild(sp);
            }
            sp.value = planId;
          } else if (sp) {
            sp.parentNode.removeChild(sp);
          }
        });
        var opts = picker.querySelectorAll('.saif-plans__opt');
        Array.prototype.forEach.call(opts, function (opt) {
          var input = opt.querySelector('input');
          opt.classList.toggle('is-active', !!(input && input.checked));
        });
        /* The hero's 30-day-guarantee line disappears the moment an instalment
           plan is selected (plans are non-refundable) and returns on pay-in-full.
           Non-plan geos never reveal the picker, so they keep it permanently. */
        Array.prototype.forEach.call(document.querySelectorAll('[data-saif-fullprice-note]'), function (el) {
          el.hidden = !!planId;
        });
      }

      picker.addEventListener('change', function (e) {
        if (e.target && e.target.type === 'radio') apply(e.target.value);
      });
      var checked = picker.querySelector('input[type="radio"]:checked');
      apply(checked ? checked.value : '');
    });
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function wireCartSwitch() {
    var buttons = document.querySelectorAll('[data-saif-plan-switch]');
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.classList.add('is-busy');
        btn.textContent = btn.getAttribute('data-busy') || '…';
        var key = btn.getAttribute('data-key');
        var qty = parseInt(btn.getAttribute('data-qty') || '1', 10) || 1;
        var plan = btn.getAttribute('data-plan') || '';
        var variantId = btn.getAttribute('data-variant') || '';

        /* Switching between paying in full and instalments, inside the cart, is one of the
           strongest signals of price sensitivity on the site — and it was going unrecorded.
           Fired BEFORE the request, because this path reloads the page on completion. */
        if (window.SaifTrack) {
          window.SaifTrack('cart_plan_switched', {
            to: plan ? 'plan' : 'pay_in_full',
            selling_plan: plan || null,
            variant: variantId || null,
            qty: qty
          });
        }

        /* Two proven paths (QA 2026-07-31): change.js with selling_plan:null strips a
           plan from a line, but silently refuses to ATTACH one — attaching only works
           as remove + re-add. Use each deterministically.

           SAIF_PLAN_PROPS_V1 (2026-08-02) — the re-add MUST carry the line's properties
           forward. It did not, which is why booking lines had to be locked out of this
           offer entirely: a session line carries _booking_data, the payload that reserves
           the slot, and a bare re-add dropped it. The order then paid for a session with
           no date — the worker's PAID_BOOKING_SERVICE_WITHOUT_BOOKING_DATA path, money
           taken and nothing reserved. Reading the properties off /cart.js first and
           passing them back through add.js keeps the reservation intact, which is what
           lets one-on-one and the 5-lesson package be offered a plan in the cart at all
           (owner, 2026-08-02).

           Ordered remove-then-add rather than add-then-remove on purpose: bookings dedupe
           by slot, so two lines holding the same slot is a state worth never creating. The
           cost is a window where the line is gone, so the add has an explicit rollback —
           if it fails, the line goes back WITHOUT the plan rather than staying deleted. */
        var op;
        if (plan) {
          var carried = null;
          op = fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (c) {
              var line = (c.items || []).filter(function (i) { return i.key === key; })[0];
              carried = (line && line.properties) || null;
              return fetch(cartRoot() + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
              });
            })
            .then(function () {
              var body = { id: parseInt(variantId, 10), quantity: qty, selling_plan: parseInt(plan, 10) };
              if (carried) body.properties = carried;
              return fetch(cartRoot() + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body)
              }).then(function (res) {
                if (res.ok) return res;
                /* Put the line back as it was rather than leave the shopper short one item. */
                var back = { id: parseInt(variantId, 10), quantity: qty };
                if (carried) back.properties = carried;
                return fetch(cartRoot() + 'cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify(back)
                });
              });
            });
        } else {
          op = fetch(cartRoot() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty, selling_plan: null })
          });
        }
        op.then(function () { (typeof window.SaifCartRender === 'function' ? window.SaifCartRender() : window.location.reload()); })
          .catch(function () { (typeof window.SaifCartRender === 'function' ? window.SaifCartRender() : window.location.reload()); });
      });
    });
  }

  /* SAIF_CART_PLAN_V2 — the whole-cart split offer.
   *
   * A selling plan attaches to a PRODUCT, so two courses can never share one; the only
   * way to put a multi-course cart on a plan is to become the matching bundle. This
   * swaps every course line for that bundle in one pass and reloads ONCE — the cascade
   * of reloads is exactly what the owner asked to be rid of on 2026-08-02.
   *
   * Gift and booking lines are never touched: a gifted course is a legitimate separate
   * line, and a booking line carries the _booking_data payload that reserves the slot.
   */
  function wireCartPlanAll() {
    var buttons = document.querySelectorAll('[data-saif-cart-plan-all]');
    if (!buttons.length) return;
    var cat = window.SaifCourseCatalog;
    if (!cat) return;

    var WORDS = { 'foundation-of-recitation': 'foundation', 'precision-of-recitation': 'precision', 'mastery-of-recitation': 'mastery' };
    var ALL = Object.keys(WORDS);

    function coursesIn(it) {
      if (it.properties && (it.properties._gift || it.properties._booking_data)) return null;
      if (Object.prototype.hasOwnProperty.call(cat.singles, it.handle)) return [it.handle];
      if (it.handle === cat.crp.handle) return ALL.slice();
      if (it.handle === cat.byp.handle) {
        var t = String(it.variant_title || it.title || '').toLowerCase();
        return ALL.filter(function (h) { return t.indexOf(WORDS[h]) !== -1; });
      }
      return null;
    }

    /* Which bundle covers exactly this set? Three is the Complete Path; two is the
       Build Your Path variant whose title names both courses. */
    function targetFor(handles) {
      if (handles.length >= 3) return cat.crp.id;
      var v = (cat.byp.variants || []).filter(function (vv) {
        var t = String(vv.title || '').toLowerCase();
        return handles.every(function (h) { return t.indexOf(WORDS[h]) !== -1; });
      })[0];
      return v ? v.id : 0;
    }

    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.classList.add('is-busy');
        btn.textContent = btn.getAttribute('data-busy') || '…';
        var plan = parseInt(btn.getAttribute('data-plan') || '0', 10);

        fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart || !cart.items) throw new Error('no cart');
            var updates = {};
            var seen = {};
            cart.items.forEach(function (it) {
              var hs = coursesIn(it);
              if (!hs || !hs.length) return;
              hs.forEach(function (h) { seen[h] = true; });
              updates[it.key] = 0;
            });
            var handles = ALL.filter(function (h) { return seen[h]; });
            var target = targetFor(handles);
            if (!target) throw new Error('no bundle covers this set');

            /* One request strips every course line. The bundle needs its own call
               because /cart/update.js cannot attach a selling plan — but both land
               before the single reload below, so nothing renders in between. */
            return fetch(cartRoot() + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates })
            }).then(function () {
              return fetch(cartRoot() + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: target, quantity: 1, selling_plan: plan })
              });
            }).then(function () {
              /* The cart page explains the swap after reload — the same notice the
                 normaliser uses when it converges courses into a path. */
              try { sessionStorage.setItem('saifCartCombined', 'plan'); } catch (e) {}
              (typeof window.SaifCartRender === 'function' ? window.SaifCartRender() : window.location.reload());
            });
          })
          .catch(function () {
            btn.disabled = false;
            btn.classList.remove('is-busy');
            (typeof window.SaifCartRender === 'function' ? window.SaifCartRender() : window.location.reload());
          });
      });
    });
  }

  /* SAIF_BUNDLE_V1 — the pair-chooser on multi-variant products (Build Your Path).
     NOT geo-gated: the chosen variant matters for pay-in-full buyers too. Retargets every
     buy form's variant id, keeps the plan picker stamping the right variant, and re-applies
     the current plan choice after a switch. */
  function wireVariantPick() {
    var pick = document.getElementById('SaifVariantPick');
    if (!pick) return;
    var radios = pick.querySelectorAll('input[name="saif-variant-choice"]');
    if (!radios.length) return;
    var values = Array.prototype.map.call(radios, function (r) { return r.value; });
    var picker = document.getElementById('SaifPlanPicker');

    function retarget(newId) {
      var forms = document.querySelectorAll('form[action*="/cart/add"]');
      Array.prototype.forEach.call(forms, function (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && values.indexOf(idInput.value) !== -1) idInput.value = newId;
      });
      if (picker) {
        picker.setAttribute('data-variant-id', newId);
        var checkedPlan = picker.querySelector('input[name="saif-plan-choice"]:checked');
        if (checkedPlan) {
          var evt;
          try { evt = new Event('change', { bubbles: true }); } catch (e) { evt = document.createEvent('Event'); evt.initEvent('change', true, false); }
          checkedPlan.dispatchEvent(evt);
        }
      }
      Array.prototype.forEach.call(pick.querySelectorAll('.saif-variantpick__opt'), function (opt) {
        var input = opt.querySelector('input');
        opt.classList.toggle('is-active', !!(input && input.checked));
      });
      /* "What you get" must agree with the pair just chosen — otherwise the page
         ticks three courses on a two-course product (content audit, 2026-08). */
      var chosen = pick.querySelector('input[name="saif-variant-choice"]:checked');
      var label = chosen && chosen.closest('label');
      var title = (label ? label.textContent : '').toLowerCase();
      if (title) {
        Array.prototype.forEach.call(document.querySelectorAll('[data-saif-bundle-item]'), function (li) {
          li.classList.toggle('is-in', title.indexOf(li.getAttribute('data-saif-bundle-item')) !== -1);
        });
      }
    }

    pick.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'saif-variant-choice') retarget(e.target.value);
    });
  }

  function init() {
    wireCartSwitch();
    wireCartPlanAll();
    wireVariantPick();
    var gated = document.querySelectorAll('[data-saif-plans]');
    if (!gated.length) return;
    getGeo(function (j) {
      if (!j || !j.plans) return; /* stays hidden — pay-in-full is the whole page */
      Array.prototype.forEach.call(gated, function (el) { el.hidden = false; });
      /* Elements the picker replaces once it is on screen — today the hero's
         standalone price, which the picker's pay-in-full card already states. */
      Array.prototype.forEach.call(document.querySelectorAll('[data-saif-plans-hide]'), function (el) { el.hidden = true; });
      wirePicker();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
